import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertTitle,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addStuff } from "../../../redux/userRelated/userHandle";
import { underControl } from "../../../redux/userRelated/userSlice";
import { getAllSclasses, getClassDetails, getSubjectList } from "../../../redux/sclassRelated/sclassHandle";
import { resetSubjects } from "../../../redux/sclassRelated/sclassSlice";
import Popup from "../../../components/Popup";
import SetupNextStepDialog from "../../../components/SetupNextStepDialog";
import { FEEDBACK, formatAdminError, ERROR_HINTS } from "../../../utils/feedbackMessages";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { getSubjectOptionsForClass, buildAutoSubCode } from "./schoolSubjectOptions";
import { getPrimarySubjectPresets, getPrimaryPresetAddedMessage, getPrimarySubjectSuccessMessage, isPrimaryLowerStandard } from "./primarySubjectCatalog";
import { getPresetsForForm } from "./secondarySubjectCatalog";
import { getSecondaryFormLevel, getPrimaryStandardLevel, matchesPrimaryClass } from "../../../utils/classScopeMatch";
import {
    FORM3_MIN_SUBJECTS,
    isSubjectChoiceFormLevel,
    setForm3SubjectSelectionOpen,
} from "../../../utils/form3SubjectSelection";
import Form3SubmitToStudentsPanel from "../../../components/class/Form3SubmitToStudentsPanel";
import { dedupeSubjectsById, getSubjectTeacherLabel } from "../../../utils/dedupeSubjects";

const emptyRow = () => ({
    subName: "",
    sessions: "",
    isElective: false,
    mieSyllabusCode: "",
});

const presetToRow = (preset) => ({
    subName: preset.subName,
    sessions: preset.sessions,
    isElective: Boolean(preset.isElective),
    mieSyllabusCode: preset.mieSyllabusCode || "",
});

const SubjectForm = () => {
    const [subjects, setSubjects] = useState([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const userState = useSelector((state) => state.user);
    const { status, currentUser, response, error } = userState;
    const {
        sclassDetails,
        sclassesList,
        subjectsList,
        loading: classLoading,
        subloading: subjectsLoading,
    } = useSelector((state) => state.sclass);

    const sclassName = params.id;
    const adminID = currentUser._id;
    const address = "Subject";

    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [message, setMessage] = useState("");
    const [loader, setLoader] = useState(false);
    const [showNextStep, setShowNextStep] = useState(false);
    const [showForm3SubmitDialog, setShowForm3SubmitDialog] = useState(false);
    const [selectionOpen, setSelectionOpen] = useState(false);

    useEffect(() => {
        if (!sclassName) return;
        dispatch(resetSubjects());
        dispatch(getSubjectList(sclassName, "ClassSubjects"));
        dispatch(getClassDetails(sclassName, "Sclass"));
        if (adminID) {
            dispatch(getAllSclasses(adminID, "Sclass"));
        }
    }, [dispatch, sclassName, adminID]);

    const classRow = useMemo(() => {
        if (sclassDetails && typeof sclassDetails === "object" && !Array.isArray(sclassDetails)) {
            const id = sclassDetails._id || sclassDetails.id;
            if (id && (sclassDetails.sclassName || sclassDetails.levelCode)) {
                return sclassDetails;
            }
        }
        if (!sclassName || !Array.isArray(sclassesList)) return null;
        return (
            sclassesList.find((row) => String(row._id || row.id) === String(sclassName)) || null
        );
    }, [sclassDetails, sclassesList, sclassName]);

    const isPrimaryClass = useMemo(() => {
        if (classRow) return matchesPrimaryClass(classRow);
        const schoolType = currentUser?.schoolType || "";
        return schoolType === "Primary";
    }, [classRow, currentUser?.schoolType]);

    const secondaryFormLevel = useMemo(
        () => (classRow ? getSecondaryFormLevel(classRow) : null),
        [classRow],
    );

    const primaryStandardLevel = useMemo(
        () => (classRow ? getPrimaryStandardLevel(classRow) : null),
        [classRow],
    );

    const isPrimaryLower = useMemo(
        () => (classRow ? isPrimaryLowerStandard(classRow) : false),
        [classRow],
    );

    const subjectOptions = useMemo(
        () => getSubjectOptionsForClass({ isPrimaryClass, classRow }),
        [isPrimaryClass, classRow],
    );

    const existingInClass = useMemo(() => {
        if (!Array.isArray(subjectsList) || subjectsList.length === 0) return [];
        return dedupeSubjectsById(subjectsList);
    }, [subjectsList]);

    const existingNames = useMemo(
        () => new Set(existingInClass.map((s) => String(s.subName || "").trim().toLowerCase())),
        [existingInClass],
    );

    const availableSubjectOptions = useMemo(
        () => subjectOptions.filter((title) => !existingNames.has(String(title).trim().toLowerCase())),
        [subjectOptions, existingNames],
    );

    useEffect(() => {
        if (subjectsLoading) return;
        if (subjects.length > 0) return;
        setSubjects([emptyRow()]);
    }, [subjectsLoading, subjects.length]);

    const classLabel =
        classRow?.sclassName || location.state?.className || (classLoading ? "…" : "this class");
    const isSubjectChoiceClass = isSubjectChoiceFormLevel(secondaryFormLevel);
    const totalSubjectsInClass = existingInClass.length;

    useEffect(() => {
        setSelectionOpen(Boolean(classRow?.subjectSelectionOpen));
    }, [classRow?.subjectSelectionOpen]);

    const setField = (index, key, value) => {
        setSubjects((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
    };

    const handleAddSubject = () => {
        setSubjects((prev) => [...prev, emptyRow()]);
    };

    const handleRemoveSubject = (index) => {
        setSubjects((prev) => prev.filter((_, i) => i !== index));
    };

    const appendPresetRows = (presets) => {
        const fresh = presets
            .map(presetToRow)
            .filter((row) => !existingNames.has(String(row.subName || "").trim().toLowerCase()));
        if (!fresh.length) {
            setMessage(
                isPrimaryClass
                    ? `All ${isPrimaryLower ? "Std 1–4" : "Std 5–8"} preset subjects are already in this class.`
                    : "All preset subjects are already in this class.",
            );
            setPopupSeverity(undefined);
            setShowPopup(true);
            return;
        }
        setSubjects((prev) => {
            const kept = prev.filter((row) => String(row.subName || "").trim());
            return kept.length ? [...kept, ...fresh] : fresh;
        });
        if (isPrimaryClass) {
            setMessage(getPrimaryPresetAddedMessage(classRow, fresh.length));
            setPopupSeverity("success");
            setShowPopup(true);
        }
    };

    const handleAddAllPrimary = () => {
        appendPresetRows(getPrimarySubjectPresets(classRow));
    };

    const handleAddAllSecondary = () => {
        if (!secondaryFormLevel) {
            setMessage(
                "Could not detect Form 1–4 for this class. Use a class named like “Form 2” or set its level code, then try again."
            );
            setShowPopup(true);
            return;
        }
        const presets = getPresetsForForm(secondaryFormLevel);
        if (!presets.length) {
            setMessage("No subject preset found for this form level.");
            setShowPopup(true);
            return;
        }
        appendPresetRows(presets);
    };

    const submitHandler = (event) => {
        event.preventDefault();
        const newRows = subjects.filter((s) => String(s.subName || "").trim());
        if (!newRows.length) {
            setMessage("Add at least one new subject, or go back if this class already has all subjects.");
            setShowPopup(true);
            return;
        }
        const invalidRow = newRows.find((s) => !String(s.sessions || "").trim());
        if (invalidRow) {
            setMessage("Enter periods per week for every new subject row.");
            setShowPopup(true);
            return;
        }
        const duplicateInBatch = newRows.some((row, i) =>
            newRows.findIndex(
                (other, j) =>
                    j !== i &&
                    String(other.subName).trim().toLowerCase() === String(row.subName).trim().toLowerCase(),
            ) >= 0,
        );
        if (duplicateInBatch) {
            setMessage("Remove duplicate subjects in the new rows.");
            setShowPopup(true);
            return;
        }
        setLoader(true);
        dispatch(
            addStuff(
                {
                    sclassName,
                    adminID,
                    subjects: newRows.map((subject, index) => ({
                        subName: subject.subName.trim(),
                        subCode: buildAutoSubCode(sclassName, existingInClass.length + index, subject.subName.trim()),
                        sessions: String(subject.sessions),
                        isElective: isPrimaryClass ? false : Boolean(subject.isElective),
                        mieSyllabusCode: subject.mieSyllabusCode?.trim() || "",
                    })),
                },
                address,
            ),
        );
    };

    useEffect(() => {
        if (status === "added") {
            dispatch(getSubjectList(sclassName, "ClassSubjects"));
            dispatch(underControl());
            setLoader(false);
            if (isSubjectChoiceClass) {
                setShowForm3SubmitDialog(true);
                setMessage(
                    `${FEEDBACK.subjectAdded} You can now mark this ${secondaryFormLevel} class ready so pupils choose subjects at enrollment.`,
                );
            } else if (isPrimaryClass) {
                setMessage(getPrimarySubjectSuccessMessage(classRow));
                setPopupSeverity("success");
                setShowPopup(true);
                setShowNextStep(true);
            } else {
                setMessage(`${FEEDBACK.subjectAdded}\nProceed to the next setup step.`);
                setShowNextStep(true);
            }
            return;
        } else if (status === "failed") {
            setMessage(formatAdminError(response));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(formatAdminError(error, ERROR_HINTS.network));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, navigate, error, response, dispatch, sclassName, isSubjectChoiceClass, secondaryFormLevel, isPrimaryClass, isPrimaryLower, classRow]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.secondary.main, 0.06)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.secondary.dark} 0%, ${t.palette.secondary.main} 50%, ${alpha(
                                    t.palette.secondary.light,
                                    0.9
                                )} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <PostAddIcon sx={{ fontSize: 30, opacity: 0.95 }} />
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    Curriculum
                                </Typography>
                                <Typography variant="h5" fontWeight={800}>
                                    Add subjects — {classLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                                    {isPrimaryClass
                                        ? "Primary subjects are all compulsory. Use “Add all primary subjects” or add rows one by one."
                                        : "Pick subjects, set periods per week, and mark compulsory vs elective. MIE syllabus codes are optional."}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box component="form" onSubmit={submitHandler} sx={{ p: { xs: 2, sm: 3 } }}>
                        {isSubjectChoiceClass ? (
                            <Form3SubmitToStudentsPanel
                                formLevel={secondaryFormLevel}
                                classId={sclassName}
                                subjectCount={totalSubjectsInClass}
                                selectionOpen={selectionOpen}
                                onUpdated={(open) => {
                                    setSelectionOpen(open);
                                    dispatch(getClassDetails(sclassName, "Sclass"));
                                }}
                            />
                        ) : null}

                        {existingInClass.length > 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.04) }}>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Already in this class ({existingInClass.length})
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    These subjects are saved. Add only what is missing below.
                                </Typography>
                                <TableContainer sx={{ maxHeight: 220 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Periods/week</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {existingInClass.map((sub, idx) => (
                                                <TableRow key={sub._id || sub.id || idx}>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>{sub.subName}</TableCell>
                                                    <TableCell>
                                                        {getSubjectTeacherLabel(sub) === "Unassigned" ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Unassigned
                                                            </Typography>
                                                        ) : (
                                                            <Chip
                                                                size="small"
                                                                label={getSubjectTeacherLabel(sub)}
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{sub.sessions}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={sub.isElective ? "Elective" : "Compulsory"}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        ) : null}

                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                            {existingInClass.length ? "Add more subjects" : "Add subjects"}
                        </Typography>

                        <Stack spacing={2} sx={{ mb: 3 }}>
                            {isPrimaryClass ? (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    <AlertTitle>Primary curriculum</AlertTitle>
                                    {isPrimaryLower ? (
                                        <>
                                            Std 1–4: English, Mathematics, Chichewa, Expressive Arts, Bible
                                            Knowledge, and <strong>Luso la moyo wabwino</strong>. No Primary
                                            Science, Social Studies, or Agriculture at this level.
                                        </>
                                    ) : (
                                        <>
                                            {primaryStandardLevel || "Std 5–8"}: Mathematics, Chichewa, Primary
                                            Science, Social Studies, Agriculture, Expressive Arts, Life Skills, and
                                            Bible Knowledge.
                                        </>
                                    )}
                                </Alert>
                            ) : null}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                                {isPrimaryClass ? (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<PlaylistAddIcon />}
                                        onClick={handleAddAllPrimary}
                                        sx={{ borderRadius: 2, fontWeight: 600 }}
                                    >
                                        Add all primary subjects ({getPrimarySubjectPresets(classRow).length})
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<PlaylistAddIcon />}
                                        onClick={handleAddAllSecondary}
                                        disabled={!secondaryFormLevel}
                                        sx={{ borderRadius: 2, fontWeight: 600 }}
                                    >
                                        {secondaryFormLevel
                                            ? `Add missing ${secondaryFormLevel} subjects`
                                            : "Add all form subjects (Form 1–4 class required)"}
                                    </Button>
                                )}
                                <Button variant="outlined" onClick={handleAddSubject} sx={{ borderRadius: 2 }}>
                                    Add one subject row
                                </Button>
                            </Stack>
                        </Stack>

                        {subjects.map((subject, index) => (
                            <Box key={index} sx={{ mb: index < subjects.length - 1 ? 3 : 0 }}>
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    Subject {index + 1}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <InputLabel id={`subject-pick-${index}`}>Subject</InputLabel>
                                            <Select
                                                labelId={`subject-pick-${index}`}
                                                label="Subject"
                                                value={subject.subName || ""}
                                                onChange={(e) => setField(index, "subName", e.target.value)}
                                            >
                                                <MenuItem value="">
                                                    <em>Select subject…</em>
                                                </MenuItem>
                                                {availableSubjectOptions.length === 0 ? (
                                                    <MenuItem disabled value="">
                                                        <em>All catalogue subjects are already added</em>
                                                    </MenuItem>
                                                ) : (
                                                    availableSubjectOptions.map((title) => (
                                                        <MenuItem key={title} value={title}>
                                                            {title}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={isPrimaryClass ? 6 : 4}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="Periods per week"
                                            value={subject.sessions}
                                            onChange={(e) => setField(index, "sessions", e.target.value)}
                                            inputProps={{ min: 1, max: 40, step: 1 }}
                                            helperText="Teaching periods scheduled per week for this subject."
                                        />
                                    </Grid>
                                    {!isPrimaryClass ? (
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <InputLabel id={`type-${index}`}>Compulsory / elective</InputLabel>
                                                <Select
                                                    labelId={`type-${index}`}
                                                    label="Compulsory / elective"
                                                    value={subject.isElective ? "elective" : "compulsory"}
                                                    onChange={(e) =>
                                                        setField(index, "isElective", e.target.value === "elective")
                                                    }
                                                >
                                                    <MenuItem value="compulsory">Compulsory</MenuItem>
                                                    <MenuItem value="elective">Elective</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ) : (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Type"
                                                value="Compulsory"
                                                disabled
                                                helperText="All primary subjects are compulsory."
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={isPrimaryClass ? 12 : 4}>
                                        <TextField
                                            fullWidth
                                            label="MIE syllabus code"
                                            value={subject.mieSyllabusCode}
                                            onChange={(e) => setField(index, "mieSyllabusCode", e.target.value)}
                                            placeholder="e.g. DSKP reference"
                                            helperText="Optional mapping to MIE / KSSR-KSSM syllabus codes."
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={1}>
                                            {index === subjects.length - 1 && (
                                                <Button variant="outlined" onClick={handleAddSubject} sx={{ borderRadius: 2 }}>
                                                    Add another subject
                                                </Button>
                                            )}
                                            {subjects.length > 1 && (
                                                <Button
                                                    variant="text"
                                                    color="error"
                                                    onClick={() => handleRemoveSubject(index)}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    Remove this row
                                                </Button>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>
                                {index < subjects.length - 1 && <Divider sx={{ mt: 3 }} />}
                            </Box>
                        ))}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
                            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loader}
                                sx={{ borderRadius: 2, minWidth: 160, fontWeight: 700 }}
                            >
                                {loader ? <CircularProgress size={26} color="inherit" /> : "Save subjects"}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
            <SetupNextStepDialog
                open={showForm3SubmitDialog}
                onClose={() => setShowForm3SubmitDialog(false)}
                title={`Mark ${secondaryFormLevel} ready for enrollment?`}
                description={`Students will see all subjects in ${classLabel}, choose at least ${FORM3_MIN_SUBJECTS}, and enroll in this class (you verify enrollment after).`}
                actions={[
                    {
                        label: "Submit to students now",
                        variant: "contained",
                        color: "success",
                        onClick: async () => {
                            setShowForm3SubmitDialog(false);
                            try {
                                const result = await setForm3SubjectSelectionOpen(sclassName, true);
                                setSelectionOpen(Boolean(result.subjectSelectionOpen));
                                setMessage(
                                    result.message ||
                                        `Class marked ready. New ${secondaryFormLevel} pupils choose subjects when they enroll.`,
                                );
                                setPopupSeverity("success");
                                setShowPopup(true);
                                dispatch(getClassDetails(sclassName, "Sclass"));
                            } catch (e) {
                                setMessage(
                                    e.response?.data?.message ||
                                        e.response?.data?.error ||
                                        e.message ||
                                        "Could not submit to students.",
                                );
                                setPopupSeverity("error");
                                setShowPopup(true);
                            }
                        },
                    },
                    {
                        label: "Do this later",
                        variant: "outlined",
                        onClick: () => {
                            setShowForm3SubmitDialog(false);
                            navigate(`/Admin/classes/class/${sclassName}`);
                        },
                    },
                ]}
            />
            <SetupNextStepDialog
                open={showNextStep}
                onClose={() => setShowNextStep(false)}
                title="Subjects saved"
                description={
                    isPrimaryClass
                        ? getPrimarySubjectSuccessMessage(classRow)
                        : "What would you like to do next?"
                }
                actions={[
                    {
                        label: "Add subjects in another class",
                        variant: "contained",
                        onClick: () => {
                            setShowNextStep(false);
                            navigate("/Admin/subjects/chooseclass");
                        },
                    },
                    {
                        label: "Next: assign teachers",
                        variant: "outlined",
                        onClick: () => {
                            setShowNextStep(false);
                            navigate("/Admin/teachers/chooseclass");
                        },
                    },
                    {
                        label: "Stay on subjects",
                        variant: "text",
                        color: "inherit",
                        onClick: () => {
                            setShowNextStep(false);
                            navigate(
                                sclassName
                                    ? `/Admin/subjects/class/${sclassName}`
                                    : "/Admin/subjects/chooseclass",
                            );
                        },
                    },
                ]}
            />
        </Box>
    );
};

export default SubjectForm;
